// src/pages/lms/LMSFees.jsx — Updated Fee System
// NEW: PDF fee slip upload, student submission tracking,
//      admin verify/reject submissions, combined manual+PDF
// KEPT: all existing logic, KuickPay, structures, vouchers
// Typing lag fixed: all modals are isolated components

import { useState, useEffect, useRef, useMemo } from 'react';
import sendNotification, {
  notifyFeeVoucher,
} from '../../services/NotificationService';
import { supabase } from '../../lib/supabase';
import { useLMSAuth } from '../../hooks/useLMSAuth';
import LMSLayout from './LMSLayout';
import LMSPageGuard from '../../components/lms/LMSPageGuard';
import {
  Plus, X, Search, Trash2, CheckCircle, AlertCircle,
  Clock, RefreshCw, FileText, Upload, Eye, Download,
  ChevronDown, BarChart3, User,
} from 'lucide-react';

// ── TEST MODE ──────────────────────────────────────────
const KUICKPAY_TEST_MODE = true; // set false for production
// ──────────────────────────────────────────────────────

const SS = {
  pending:   { bg:'#EFF6FF', color:'#1D4ED8', border:'#BFDBFE', label:'Pending',   emoji:'🕐' },
  paid:      { bg:'#F0FDF4', color:'#065F46', border:'#BBF7D0', label:'Paid',      emoji:'✅' },
  overdue:   { bg:'#FEF2F2', color:'#991B1B', border:'#FECACA', label:'Overdue',   emoji:'⚠️' },
  waived:    { bg:'#F5F3FF', color:'#5B21B6', border:'#DDD6FE', label:'Waived',    emoji:'🎁' },
  cancelled: { bg:'#F9FAFB', color:'#6B7280', border:'#E5E7EB', label:'Cancelled', emoji:'❌' },
};
const SUBSS = {
  not_submitted:{ bg:'#F9FAFB', color:'#6B7280', label:'Not Submitted' },
  submitted:    { bg:'#FFF7ED', color:'#92400E', label:'Submitted — Pending Verify' },
  verified:     { bg:'#F0FDF4', color:'#065F46', label:'Verified ✅' },
  rejected:     { bg:'#FEF2F2', color:'#991B1B', label:'Rejected ❌' },
};
const FEE_ITEMS = ['Tuition Fee','Admission Fee','Examination Fee','Lab Fee',
  'Library Fee','Sports Fee','Transport Fee','Hostel Fee','Security Deposit',
  'Development Fund','Computer Fee','Other'];
const INP = { width:'100%', padding:'9px 12px', borderRadius:8, border:'1.5px solid #E5E7EB',
  fontSize:13, boxSizing:'border-box', outline:'none', color:'#111827' };

// ── Fee Structure Form Modal ───────────────────────────
function StructureFormModal({ editStruct, institutionId, account, onSave, onClose }) {
  const [saving,  setSaving]  = useState(false);
  const [error,   setError]   = useState('');
  const [items,   setItems]   = useState(editStruct ? [] : [{ name:'Tuition Fee', amount:'' }]);
  const nameRef    = useRef(); const descRef     = useRef();
  const yearRef    = useRef(); const dueRef      = useRef();
  const kpIdRef    = useRef(); const kpKeyRef    = useRef();

  useEffect(() => {
    if (editStruct) {
      supabase.from('lms_fee_items').select('*').eq('fee_structure_id', editStruct.id)
        .then(({ data }) => setItems(data?.map(i => ({ id:i.id, name:i.name, amount:i.amount })) || []));
    }
  }, [editStruct]);

  const save = async () => {
    const name = nameRef.current?.value?.trim() || '';
    if (!name) { setError('Structure name is required'); return; }
    if (!items.length || items.some(i => !i.name || !i.amount)) {
      setError('All fee items need a name and amount'); return;
    }
    setSaving(true); setError('');
    try {
      const total = items.reduce((a, i) => a + (Number(i.amount) || 0), 0);
      const data  = {
        institution_id:          institutionId,
        name,
        description:             descRef.current?.value?.trim() || null,
        academic_year:           yearRef.current?.value?.trim() || null,
        due_date:                dueRef.current?.value || null,
        kuickpay_institution_id: kpIdRef.current?.value?.trim() || null,
        kuickpay_secured_key:    kpKeyRef.current?.value?.trim() || null,
        is_active: true,
      };
      let structId;
      if (editStruct) {
        await supabase.from('lms_fee_structures').update(data).eq('id', editStruct.id);
        structId = editStruct.id;
        await supabase.from('lms_fee_items').delete().eq('fee_structure_id', structId);
      } else {
        const { data: s, error: e } = await supabase.from('lms_fee_structures').insert(data).select().single();
        if (e) throw e;
        structId = s.id;
      }
      await supabase.from('lms_fee_items').insert(
        items.map(i => ({ fee_structure_id: structId, name: i.name, amount: Number(i.amount) }))
      );
      onSave(editStruct ? 'Fee structure updated!' : 'Fee structure created!');
    } catch(e) { setError(e.message || 'Save failed'); }
    finally { setSaving(false); }
  };

  const F = ({ label, children, col }) => (
    <div style={{ gridColumn: col || 'span 1' }}>
      <label style={{ fontSize:12, fontWeight:700, color:'#374151', display:'block', marginBottom:5 }}>{label}</label>
      {children}
    </div>
  );

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}>
      <div style={{ background:'#fff', borderRadius:20, width:'100%', maxWidth:640, maxHeight:'92vh', overflowY:'auto' }}>
        <div style={{ padding:'20px 24px', borderBottom:'1px solid #E5E7EB', display:'flex', alignItems:'center', justifyContent:'space-between', position:'sticky', top:0, background:'#fff', zIndex:1 }}>
          <h3 style={{ margin:0, fontWeight:800, fontSize:18, color:'#111827' }}>{editStruct ? 'Edit Structure' : 'Create Fee Structure'}</h3>
          <button onClick={onClose} style={{ background:'none', border:'none', cursor:'pointer' }}><X size={22} color="#6B7280"/></button>
        </div>
        <div style={{ padding:24 }}>
          {error && <div style={{ background:'#FEF2F2', border:'1px solid #FECACA', borderRadius:8, padding:'10px 14px', marginBottom:16, fontSize:13, color:'#991B1B', fontWeight:600 }}>❌ {error}</div>}
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
            <F label="Structure Name *" col="span 2"><input ref={nameRef} defaultValue={editStruct?.name||''} placeholder="e.g. Semester 1 Fee 2024-25" style={INP}/></F>
            <F label="Description"><input ref={descRef} defaultValue={editStruct?.description||''} placeholder="Optional description" style={INP}/></F>
            <F label="Academic Year"><input ref={yearRef} defaultValue={editStruct?.academic_year||''} placeholder="e.g. 2024-25" style={INP}/></F>
            <F label="Due Date" col="span 2"><input ref={dueRef} type="date" defaultValue={editStruct?.due_date||''} style={INP}/></F>
            <F label="KuickPay Institution ID"><input ref={kpIdRef} defaultValue={editStruct?.kuickpay_institution_id||''} placeholder="Leave blank for manual" style={INP}/></F>
            <F label="KuickPay Secured Key"><input ref={kpKeyRef} defaultValue={editStruct?.kuickpay_secured_key||''} placeholder="Leave blank for manual" style={INP}/></F>
          </div>

          {/* Fee items */}
          <div style={{ marginTop:20 }}>
            <div style={{ fontSize:13, fontWeight:700, color:'#111827', marginBottom:10 }}>Fee Items</div>
            {items.map((item, i) => (
              <div key={i} style={{ display:'flex', gap:8, marginBottom:8, alignItems:'center' }}>
                <select value={item.name} onChange={e => { const n=[...items]; n[i]={...n[i],name:e.target.value}; setItems(n); }}
                  style={{ ...INP, flex:2 }}>
                  {FEE_ITEMS.map(f => <option key={f} value={f}>{f}</option>)}
                </select>
                <input type="number" value={item.amount} placeholder="Amount (Rs)" min="0"
                  onChange={e => { const n=[...items]; n[i]={...n[i],amount:e.target.value}; setItems(n); }}
                  style={{ ...INP, flex:1 }}/>
                <button onClick={() => setItems(items.filter((_,j)=>j!==i))}
                  style={{ padding:'9px 10px', borderRadius:8, border:'1px solid #FECACA', background:'#FEF2F2', cursor:'pointer', flexShrink:0 }}>
                  <X size={14} color="#EF4444"/>
                </button>
              </div>
            ))}
            <button onClick={() => setItems([...items, { name:'Tuition Fee', amount:'' }])}
              style={{ width:'100%', padding:'9px 0', borderRadius:8, border:'1.5px dashed #BFDBFE', background:'#EFF6FF', color:'#1D4ED8', fontSize:13, fontWeight:700, cursor:'pointer', marginBottom:8 }}>
              + Add Fee Item
            </button>
            {items.length > 0 && (
              <div style={{ textAlign:'right', fontSize:14, fontWeight:800, color:'#111827' }}>
                Total: Rs {items.reduce((a,i)=>a+(Number(i.amount)||0),0).toLocaleString()}
              </div>
            )}
          </div>

          <div style={{ display:'flex', gap:10, marginTop:20 }}>
            <button onClick={onClose} style={{ flex:1, padding:'12px 0', borderRadius:10, border:'1.5px solid #E5E7EB', background:'#fff', color:'#374151', fontWeight:600, cursor:'pointer' }}>Cancel</button>
            <button onClick={save} disabled={saving} style={{ flex:2, padding:'12px 0', borderRadius:10, border:'none', background:saving?'#E5E7EB':'linear-gradient(135deg,#4F8EF7,#7C3AED)', color:saving?'#9CA3AF':'#fff', fontWeight:700, cursor:saving?'wait':'pointer', fontSize:14 }}>
              {saving ? 'Saving...' : editStruct ? 'Update Structure' : 'Create Structure'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Voucher Form Modal (Manual + PDF combined) ─────────
function VoucherFormModal({ structures, institutionId, account, onSave, onClose }) {
  const [saving,    setSaving]    = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error,     setError]     = useState('');
  const [vType,     setVType]     = useState('manual'); // manual | pdf
  const [selStruct, setSelStruct] = useState('');
  const [pdfUrl,    setPdfUrl]    = useState('');
  const [rollResult,setRollResult]= useState(null);
  const [rollLoad,  setRollLoad]  = useState(false);
  const [sName,     setSName]     = useState('');
  const [sRoll,     setSRoll]     = useState('');
  const [sUser,     setSUser]     = useState('');
  const [sPhone,    setSPhone]    = useState('');
  const [sClass,    setSClass]    = useState('');
  const rollRef     = useRef();
  const totalRef    = useRef();
  const discRef     = useRef();
  const dueRef      = useRef();
  const notesRef    = useRef();
  const pdfTitleRef = useRef();

  const selectedStruct = structures.find(s => s.id === selStruct);

  const lookupRoll = async () => {
    const roll = rollRef.current?.value?.trim() || '';
    if (!roll) return;
    setRollLoad(true); setRollResult(null);
    try {
      const { data } = await supabase.rpc('find_student_by_roll_no', { p_institution_id: institutionId, p_roll_no: roll });
      if (data?.found) {
        setSName(data.full_name || '');
        setSRoll(data.roll_no   || roll);
        setSUser(data.username  || '');
        setSPhone(data.phone    || '');
      }
      setRollResult(data);
    } catch(_) { setRollResult({ found: false }); }
    finally { setRollLoad(false); }
  };

  const uploadPdf = async (file) => {
    if (!file || !file.name.endsWith('.pdf')) { setError('Please upload a PDF file'); return; }
    setUploading(true);
    try {
      const path = `${institutionId}/fees/${Date.now()}_${file.name}`;
      await supabase.storage.from('lms-fees').upload(path, file, { upsert:true });
      const { data:{ publicUrl } } = supabase.storage.from('lms-fees').getPublicUrl(path);
      setPdfUrl(publicUrl);
    } catch(e) { setError('PDF upload failed: '+e.message); }
    finally { setUploading(false); }
  };

  const save = async () => {
    if (vType === 'manual') {
      if (!sName.trim())   { setError('Student name is required'); return; }
      if (!selStruct)      { setError('Select a fee structure');   return; }
    } else {
      if (!pdfUrl)         { setError('Upload a PDF fee slip first'); return; }
      if (!sName.trim())   { setError('Student name is required');    return; }
    }
    setSaving(true); setError('');
    try {
      const struct    = structures.find(s => s.id === selStruct);
      const total     = vType === 'manual'
        ? (Number(totalRef.current?.value) || struct?.total_amount || 0)
        : (Number(totalRef.current?.value) || 0);
      const discount  = Number(discRef.current?.value) || 0;
      const data = {
        institution_id:      institutionId,
        fee_structure_id:    selStruct || null,
        fee_structure_name:  vType === 'pdf'
          ? (pdfTitleRef.current?.value?.trim() || 'Fee Slip')
          : (struct?.name || ''),
        student_name:        sName.trim(),
        student_roll_number: sRoll.trim() || null,
        student_username:    sUser.trim() || null,
        student_phone:       sPhone.trim() || null,
        student_class:       sClass.trim() || null,
        total_amount:        total,
        discount_amount:     discount,
        due_date:            dueRef.current?.value || null,
        status:              'pending',
        fee_slip_type:       vType,
        fee_slip_pdf_url:    vType === 'pdf' ? pdfUrl : null,
        submission_status:   'not_submitted',
        notes:               notesRef.current?.value?.trim() || null,
      };
      // Link student_id if found in app
      if (rollResult?.found) {
        data.student_id    = rollResult.id;
        data.student_email = rollResult.email || null;
      }
      const { error:e } = await supabase.from('lms_fee_vouchers').insert(data);
      if (e) throw e;

      if (data.student_id) {
        await notifyFeeVoucher(institutionId, {
          id: data.id,
          fee_structure_name: data.fee_structure_name,
          final_amount: data.final_amount,
          due_date: data.due_date,
        }, [data.student_id]);
      }

      onSave(vType === 'pdf' ? 'PDF fee slip uploaded & assigned!' : 'Fee voucher generated!');
    } catch(e) { setError(e.message || 'Save failed'); }
    finally { setSaving(false); }
  };

  const F = ({ label, children, col }) => (
    <div style={{ gridColumn: col || 'span 1' }}>
      <label style={{ fontSize:12, fontWeight:700, color:'#374151', display:'block', marginBottom:5 }}>{label}</label>
      {children}
    </div>
  );

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}>
      <div style={{ background:'#fff', borderRadius:20, width:'100%', maxWidth:600, maxHeight:'94vh', overflowY:'auto' }}>
        <div style={{ padding:'20px 24px', borderBottom:'1px solid #E5E7EB', display:'flex', alignItems:'center', justifyContent:'space-between', position:'sticky', top:0, background:'#fff', zIndex:1 }}>
          <h3 style={{ margin:0, fontWeight:800, fontSize:18, color:'#111827' }}>Generate Fee Voucher</h3>
          <button onClick={onClose} style={{ background:'none', border:'none', cursor:'pointer' }}><X size={22} color="#6B7280"/></button>
        </div>
        <div style={{ padding:24 }}>
          {error && <div style={{ background:'#FEF2F2', border:'1px solid #FECACA', borderRadius:8, padding:'10px 14px', marginBottom:16, fontSize:13, color:'#991B1B', fontWeight:600 }}>❌ {error}</div>}

          {/* Type selector */}
          <div style={{ display:'flex', background:'#F1F5F9', borderRadius:10, padding:3, marginBottom:20, gap:2 }}>
            {[{ k:'manual', l:'📋 Manual Entry', d:'Enter fee details manually' },
              { k:'pdf',    l:'📄 Upload PDF',   d:'Upload bank challan or fee slip PDF' }].map(t => (
              <button key={t.k} onClick={() => setVType(t.k)}
                style={{ flex:1, padding:'10px 8px', borderRadius:8, border:'none', background:vType===t.k?'#fff':'transparent', color:vType===t.k?'#1E293B':'#64748B', fontWeight:700, fontSize:13, cursor:'pointer', boxShadow:vType===t.k?'0 1px 4px rgba(0,0,0,0.08)':'none', textAlign:'center' }}>
                <div>{t.l}</div>
                <div style={{ fontSize:10, fontWeight:400, color:vType===t.k?'#6B7280':'#94A3B8', marginTop:1 }}>{t.d}</div>
              </button>
            ))}
          </div>

          {/* Roll number lookup (both types) */}
          <div style={{ background:'linear-gradient(135deg,#EFF6FF,#F5F3FF)', border:'1px solid #BFDBFE', borderRadius:12, padding:14, marginBottom:18 }}>
            <div style={{ fontSize:12, fontWeight:700, color:'#1D4ED8', marginBottom:8 }}>🔍 Lookup Student by Roll Number (auto-fills details from app)</div>
            <div style={{ display:'flex', gap:8 }}>
              <input ref={rollRef} defaultValue="" onKeyDown={e=>e.key==='Enter'&&lookupRoll()} placeholder="Enter roll number..." style={{ ...INP, flex:1 }}/>
              <button onClick={lookupRoll} disabled={rollLoad} style={{ padding:'9px 16px', borderRadius:8, border:'none', background:'#4F8EF7', color:'#fff', fontWeight:700, fontSize:13, cursor:'pointer', whiteSpace:'nowrap' }}>
                {rollLoad ? '...' : 'Lookup'}
              </button>
            </div>
            {rollResult && (
              <div style={{ marginTop:8, fontSize:12, color:rollResult.found?'#065F46':'#6B7280', fontWeight:600 }}>
                {rollResult.found ? `✅ Found: ${rollResult.full_name} — details filled. Student will see this voucher in their app.` : '❌ Not found in app. You can still fill details manually — student will be linked when they set their roll number.'}
              </div>
            )}
          </div>

          {/* PDF upload section */}
          {vType === 'pdf' && (
            <div style={{ background:'#F8FAFF', border:'1.5px dashed #BFDBFE', borderRadius:12, padding:16, marginBottom:18, textAlign:'center' }}>
              {pdfUrl ? (
                <div style={{ display:'flex', alignItems:'center', gap:10, justifyContent:'center' }}>
                  <FileText size={24} color="#4F8EF7"/>
                  <div>
                    <div style={{ fontSize:13, fontWeight:700, color:'#111827' }}>PDF Uploaded ✅</div>
                    <a href={pdfUrl} target="_blank" rel="noreferrer" style={{ fontSize:12, color:'#4F8EF7' }}>View PDF</a>
                    <button onClick={() => setPdfUrl('')} style={{ marginLeft:10, fontSize:11, color:'#EF4444', background:'none', border:'none', cursor:'pointer' }}>Remove</button>
                  </div>
                </div>
              ) : (
                <label style={{ cursor:'pointer' }}>
                  <input type="file" accept=".pdf" style={{ display:'none' }}
                    onChange={e => e.target.files?.[0] && uploadPdf(e.target.files[0])}/>
                  <div>
                    <Upload size={32} color="#BFDBFE" style={{ marginBottom:8 }}/>
                    <div style={{ fontSize:13, fontWeight:700, color:'#374151' }}>{uploading ? 'Uploading...' : 'Click to upload fee slip PDF'}</div>
                    <div style={{ fontSize:11, color:'#9CA3AF', marginTop:4 }}>Bank challan, university fee slip, etc.</div>
                  </div>
                </label>
              )}
              <div style={{ marginTop:12 }}>
                <label style={{ fontSize:12, fontWeight:700, color:'#374151', display:'block', marginBottom:5 }}>PDF Title (shown to student)</label>
                <input ref={pdfTitleRef} defaultValue="" placeholder="e.g. Semester 1 Bank Challan 2024-25" style={INP}/>
              </div>
            </div>
          )}

          {/* Manual structure selector */}
          {vType === 'manual' && (
            <div style={{ marginBottom:14 }}>
              <label style={{ fontSize:12, fontWeight:700, color:'#374151', display:'block', marginBottom:5 }}>Fee Structure *</label>
              <select value={selStruct} onChange={e => setSelStruct(e.target.value)} style={INP}>
                <option value="">— Select fee structure —</option>
                {structures.map(s => <option key={s.id} value={s.id}>{s.name} {s.academic_year?`(${s.academic_year})`:''}</option>)}
              </select>
              {selectedStruct && (
                <div style={{ marginTop:8, background:'#F0FDF4', border:'1px solid #BBF7D0', borderRadius:8, padding:'8px 12px', fontSize:12, color:'#065F46', fontWeight:600 }}>
                  Total: Rs {(selectedStruct.total_amount||0).toLocaleString()} · Due: {selectedStruct.due_date||'Not set'}
                </div>
              )}
            </div>
          )}

          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
            <F label="Student Full Name *" col="span 2">
              <input value={sName} onChange={e=>setSName(e.target.value)} placeholder="Full name" style={INP}/>
            </F>
            <F label="Roll Number">
              <input value={sRoll} onChange={e=>setSRoll(e.target.value)} placeholder="e.g. 2024-CS-001" style={INP}/>
            </F>
            <F label="Username (app)">
              <input value={sUser} onChange={e=>setSUser(e.target.value)} placeholder="App username" style={INP}/>
            </F>
            <F label="Phone">
              <input value={sPhone} onChange={e=>setSPhone(e.target.value)} placeholder="03xx-xxxxxxx" style={INP}/>
            </F>
            <F label="Class">
              <input value={sClass} onChange={e=>setSClass(e.target.value)} placeholder="e.g. BS-CS-6A" style={INP}/>
            </F>
            <F label={vType==='pdf'?'Fee Amount (Rs) *':'Override Amount (Rs)'}>
              <input ref={totalRef} type="number" min="0"
                defaultValue={vType==='pdf'?'':''} placeholder={vType==='manual'?'Leave blank to use structure total':''} style={INP}/>
            </F>
            <F label="Discount (Rs)">
              <input ref={discRef} type="number" min="0" defaultValue="" placeholder="0" style={INP}/>
            </F>
            <F label="Due Date" col="span 2">
              <input ref={dueRef} type="date" defaultValue="" style={INP}/>
            </F>
            <F label="Notes" col="span 2">
              <input ref={notesRef} defaultValue="" placeholder="Optional admin notes..." style={INP}/>
            </F>
          </div>

          <div style={{ display:'flex', gap:10, marginTop:20 }}>
            <button onClick={onClose} style={{ flex:1, padding:'12px 0', borderRadius:10, border:'1.5px solid #E5E7EB', background:'#fff', color:'#374151', fontWeight:600, cursor:'pointer' }}>Cancel</button>
            <button onClick={save} disabled={saving}
              style={{ flex:2, padding:'12px 0', borderRadius:10, border:'none', background:saving?'#E5E7EB':'linear-gradient(135deg,#4F8EF7,#7C3AED)', color:saving?'#9CA3AF':'#fff', fontWeight:700, cursor:saving?'wait':'pointer', fontSize:14 }}>
              {saving ? 'Saving...' : vType==='pdf' ? '📄 Upload & Assign Slip' : '💰 Generate Voucher'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Submission Review Modal ────────────────────────────
function SubmissionModal({ voucher, account, onSave, onClose }) {
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState('');
  const rejectRef = useRef();

  const verify = async () => {
    setSaving(true);
    try {
      await supabase.from('lms_fee_vouchers').update({
        submission_status:      'verified',
        status:                 'paid',
        submission_verified_at: new Date().toISOString(),
        submission_verified_by: account?.lms_username || 'Admin',
        payment_method:         voucher.submission_method || 'submitted',
        payment_reference:      voucher.submission_reference || null,
        payment_date:           new Date().toISOString(),
        payment_confirmed_by:   account?.lms_username || 'Admin',
      }).eq('id', voucher.id);
      await supabase.from('lms_fee_submission_log').insert({
        institution_id: voucher.institution_id,
        voucher_id:     voucher.id,
        action:         'verified',
        actor_type:     'admin',
        actor_name:     account?.lms_username || 'Admin',
        note:           'Payment verified and voucher marked as paid.',
      });

      await sendNotification({
        institution_id: voucher.institution_id,
        recipient_ids: [voucher.student_id],
        title: '✅ Fee Payment Verified!',
        body: `Your payment for ${voucher.fee_structure_name} has been verified.`,
        notification_type: 'fee',
      });

      onSave('✅ Payment verified! Voucher marked as paid.');
    } catch(e) { setError(e.message); }
    finally { setSaving(false); }
  };

  const reject = async () => {
    const note = rejectRef.current?.value?.trim() || '';
    if (!note) { setError('Please enter a reason for rejection'); return; }
    setSaving(true);
    try {
      await supabase.from('lms_fee_vouchers').update({
        submission_status:        'rejected',
        submission_rejection_note: note,
      }).eq('id', voucher.id);
      await supabase.from('lms_fee_submission_log').insert({
        institution_id: voucher.institution_id,
        voucher_id:     voucher.id,
        action:         'rejected',
        actor_type:     'admin',
        actor_name:     account?.lms_username || 'Admin',
        note,
      });
      onSave('❌ Submission rejected. Student will be notified.');
    } catch(e) { setError(e.message); }
    finally { setSaving(false); }
  };

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.55)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}>
      <div style={{ background:'#fff', borderRadius:20, width:'100%', maxWidth:520, maxHeight:'90vh', overflowY:'auto' }}>
        <div style={{ padding:'20px 24px', borderBottom:'1px solid #E5E7EB', display:'flex', alignItems:'center', justifyContent:'space-between', position:'sticky', top:0, background:'#fff', zIndex:1 }}>
          <h3 style={{ margin:0, fontWeight:800, fontSize:17, color:'#111827' }}>Review Payment Submission</h3>
          <button onClick={onClose} style={{ background:'none', border:'none', cursor:'pointer' }}><X size={20} color="#6B7280"/></button>
        </div>
        <div style={{ padding:24 }}>
          {error && <div style={{ background:'#FEF2F2', border:'1px solid #FECACA', borderRadius:8, padding:'10px', marginBottom:14, fontSize:13, color:'#991B1B' }}>❌ {error}</div>}

          {/* Student info */}
          <div style={{ background:'#F8FAFF', border:'1px solid #E2E8F0', borderRadius:12, padding:14, marginBottom:16 }}>
            <div style={{ fontWeight:800, color:'#111827', fontSize:15 }}>{voucher.student_name}</div>
            <div style={{ fontSize:12, color:'#6B7280', marginTop:2, display:'flex', gap:10, flexWrap:'wrap' }}>
              {voucher.student_roll_number && <span>Roll# {voucher.student_roll_number}</span>}
              {voucher.student_class && <span>Class: {voucher.student_class}</span>}
            </div>
            <div style={{ marginTop:8, fontSize:14, fontWeight:800, color:'#1D4ED8' }}>
              {voucher.fee_structure_name} — Rs {Number(voucher.final_amount||voucher.total_amount).toLocaleString()}
            </div>
          </div>

          {/* Submission details */}
          <div style={{ background:'#FFF7ED', border:'1px solid #FED7AA', borderRadius:12, padding:14, marginBottom:16 }}>
            <div style={{ fontWeight:700, color:'#92400E', marginBottom:10 }}>📋 Student Submission Details</div>
            {[
              ['Payment Method', voucher.submission_method?.replace('_',' ').toUpperCase() || '—'],
              ['Reference / TID', voucher.submission_reference || '—'],
              ['Student Note', voucher.submission_note || '—'],
              ['Submitted At', voucher.submitted_at ? new Date(voucher.submitted_at).toLocaleString() : '—'],
            ].map(([k,v]) => (
              <div key={k} style={{ display:'flex', justifyContent:'space-between', padding:'5px 0', borderBottom:'1px solid #FED7AA', fontSize:13 }}>
                <span style={{ color:'#78350F', fontWeight:600 }}>{k}</span>
                <span style={{ color:'#1E293B', fontWeight:700, maxWidth:'60%', textAlign:'right' }}>{v}</span>
              </div>
            ))}
          </div>

          {/* Screenshot */}
          {voucher.submission_screenshot_url && (
            <div style={{ marginBottom:16 }}>
              <div style={{ fontSize:12, fontWeight:700, color:'#374151', marginBottom:8 }}>📸 Payment Screenshot</div>
              <img src={voucher.submission_screenshot_url} alt="Payment screenshot"
                style={{ width:'100%', borderRadius:10, border:'1px solid #E5E7EB', maxHeight:280, objectFit:'contain', background:'#F9FAFB' }}/>
              <a href={voucher.submission_screenshot_url} target="_blank" rel="noreferrer"
                style={{ display:'block', marginTop:6, fontSize:12, color:'#4F8EF7', textAlign:'center' }}>View Full Image</a>
            </div>
          )}

          {/* Reject reason */}
          <div style={{ marginBottom:16 }}>
            <label style={{ fontSize:12, fontWeight:700, color:'#374151', display:'block', marginBottom:5 }}>Rejection Reason (required only if rejecting)</label>
            <input ref={rejectRef} defaultValue="" placeholder="e.g. Wrong amount, unclear screenshot..." style={INP}/>
          </div>

          <div style={{ display:'flex', gap:10 }}>
            <button onClick={reject} disabled={saving}
              style={{ flex:1, padding:'12px 0', borderRadius:10, border:'1px solid #FECACA', background:'#FEF2F2', color:'#EF4444', fontWeight:700, cursor:saving?'wait':'pointer', fontSize:13 }}>
              {saving ? '...' : '❌ Reject'}
            </button>
            <button onClick={verify} disabled={saving}
              style={{ flex:2, padding:'12px 0', borderRadius:10, border:'none', background:saving?'#E5E7EB':'linear-gradient(135deg,#10B981,#059669)', color:saving?'#9CA3AF':'#fff', fontWeight:700, cursor:saving?'wait':'pointer', fontSize:14 }}>
              {saving ? 'Processing...' : '✅ Verify & Mark Paid'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── MAIN COMPONENT ────────────────────────────────────
export default function LMSFees() {
  const { account, institution } = useLMSAuth();
  const [tab,        setTab]        = useState('vouchers');
  const [structures, setStructures] = useState([]);
  const [vouchers,   setVouchers]   = useState([]);
  const [stats,      setStats]      = useState(null);
  const [loading,    setLoading]    = useState(true);
  const [success,    setSuccess]    = useState('');
  const [error,      setError]      = useState('');
  const [showStructForm,  setShowStructForm]  = useState(false);
  const [showVoucherForm, setShowVoucherForm] = useState(false);
  const [showSubModal,    setShowSubModal]    = useState(false);
  const [editStruct,   setEditStruct]   = useState(null);
  const [selVoucher,   setSelVoucher]   = useState(null);
  const [statusF,      setStatusF]      = useState('');
  const [subStatusF,   setSubStatusF]   = useState('');
  const [typeF,        setTypeF]        = useState('');
  const [searchInput,  setSearchInput]  = useState('');
  const [search,       setSearch]       = useState('');
  const sTimer = useRef();
  const hs = v => { setSearchInput(v); clearTimeout(sTimer.current); sTimer.current=setTimeout(()=>setSearch(v),200); };

  useEffect(() => { if (institution?.id) loadAll(); }, [institution?.id]);

  const loadAll = async () => {
    setLoading(true);
    try {
      const id = institution.id;
      const [strRes, vouRes, statRes] = await Promise.all([
        supabase.from('lms_fee_structures').select('*, lms_fee_items(*)').eq('institution_id',id).eq('is_active',true).order('created_at',{ascending:false}),
        supabase.from('lms_fee_vouchers').select('*').eq('institution_id',id).order('created_at',{ascending:false}),
        supabase.rpc('get_fee_stats',{p_institution_id:id}).then(r=>r.data).catch(()=>null),
      ]);
      // Compute total_amount per structure from items
      const structs = (strRes.data||[]).map(s => ({
        ...s,
        total_amount: (s.lms_fee_items||[]).reduce((a,i)=>a+(Number(i.amount)||0),0),
      }));
      setStructures(structs);
      setVouchers(vouRes.data||[]);
      setStats(statRes);
    } catch(e) { console.error(e); setError('Load failed: '+e.message); }
    finally { setLoading(false); }
  };

  const onSaved = async (msg) => {
    setSuccess(msg); setShowStructForm(false); setShowVoucherForm(false);
    setShowSubModal(false); setEditStruct(null); setSelVoucher(null);
    setTimeout(()=>setSuccess(''),5000);
    await loadAll();
  };

  const deleteVoucher = async (id) => {
    if (!window.confirm('Delete this voucher?')) return;
    await supabase.from('lms_fee_vouchers').delete().eq('id',id);
    await loadAll();
  };

  const markPaidManually = async (v, method) => {
    await supabase.from('lms_fee_vouchers').update({
      status:'paid', payment_method:method,
      payment_confirmed_by: account?.lms_username||'Admin',
      payment_date: new Date().toISOString(),
    }).eq('id',v.id);
    setSuccess(`✅ ${v.student_name}'s fee marked as paid (${method})`);
    setTimeout(()=>setSuccess(''),4000);
    await loadAll();
  };

  const filtVouchers = useMemo(() => vouchers.filter(v => {
    const mSt  = !statusF    || v.status === statusF;
    const mSub = !subStatusF || v.submission_status === subStatusF;
    const mTp  = !typeF      || v.fee_slip_type === typeF;
    if (!mSt || !mSub || !mTp) return false;
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (v.student_name||'').toLowerCase().includes(q) ||
           (v.student_roll_number||'').toLowerCase().includes(q) ||
           (v.fee_structure_name||'').toLowerCase().includes(q) ||
           (v.student_class||'').toLowerCase().includes(q);
  }), [vouchers, statusF, subStatusF, typeF, search]);

  const pendingSubs = vouchers.filter(v => v.submission_status === 'submitted').length;

  return (
    <LMSLayout>
      <LMSPageGuard pageKey="fees">

        {/* Header */}
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:20, flexWrap:'wrap', gap:12 }}>
          <div>
            <h1 style={{ fontSize:22, fontWeight:800, color:'#111827', margin:0 }}>Fee Management</h1>
            <p style={{ color:'#6B7280', fontSize:13, margin:'4px 0 0' }}>
              {stats?.total_vouchers||0} vouchers · Rs {Number(stats?.total_collected||0).toLocaleString()} collected
              {pendingSubs > 0 && <span style={{ marginLeft:10, color:'#F59E0B', fontWeight:700 }}>⚠️ {pendingSubs} submission{pendingSubs>1?'s':''} pending review</span>}
            </p>
          </div>
          <div style={{ display:'flex', gap:8 }}>
            <button onClick={()=>{setEditStruct(null);setShowStructForm(true);}}
              style={{ display:'flex', alignItems:'center', gap:6, padding:'9px 14px', borderRadius:9, border:'1px solid #E5E7EB', background:'#fff', color:'#374151', fontSize:13, fontWeight:600, cursor:'pointer' }}>
              <Plus size={14}/> Structure
            </button>
            <button onClick={()=>setShowVoucherForm(true)}
              style={{ display:'flex', alignItems:'center', gap:8, padding:'10px 18px', borderRadius:10, border:'none', background:'linear-gradient(135deg,#4F8EF7,#7C3AED)', color:'#fff', fontSize:13, fontWeight:700, cursor:'pointer' }}>
              <Plus size={16}/> Generate Voucher
            </button>
          </div>
        </div>

        {success && <div style={{ background:'#F0FDF4', border:'1px solid #BBF7D0', borderRadius:10, padding:'12px 16px', marginBottom:16, fontSize:13, color:'#065F46', fontWeight:700 }}>{success}</div>}
        {error   && <div style={{ background:'#FEF2F2', border:'1px solid #FECACA', borderRadius:10, padding:'12px 16px', marginBottom:16, fontSize:13, color:'#991B1B' }}>{error}<button onClick={()=>setError('')} style={{ float:'right', background:'none', border:'none', cursor:'pointer' }}>✕</button></div>}

        {/* Pending submissions alert */}
        {pendingSubs > 0 && (
          <div style={{ background:'#FFF7ED', border:'1.5px solid #FED7AA', borderRadius:12, padding:'12px 16px', marginBottom:16, display:'flex', alignItems:'center', gap:12 }}>
            <span style={{ fontSize:22 }}>⚠️</span>
            <div style={{ flex:1 }}>
              <div style={{ fontWeight:700, color:'#92400E', fontSize:14 }}>{pendingSubs} Student Payment Submission{pendingSubs>1?'s':''} Pending Review</div>
              <div style={{ fontSize:12, color:'#B45309' }}>Students have submitted payment proof. Review and verify to mark their fees as paid.</div>
            </div>
            <button onClick={() => { setSubStatusF('submitted'); setTab('vouchers'); }}
              style={{ padding:'7px 14px', borderRadius:8, border:'none', background:'#F59E0B', color:'#fff', fontSize:12, fontWeight:700, cursor:'pointer', whiteSpace:'nowrap' }}>
              Review Now
            </button>
          </div>
        )}

        {/* Stats */}
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(130px,1fr))', gap:12, marginBottom:20 }}>
          {[
            { l:'Total',     v:stats?.total_vouchers||0,                                      c:'#4F8EF7', e:'📋' },
            { l:'Collected', v:`Rs ${Number(stats?.total_collected||0).toLocaleString()}`,    c:'#10B981', e:'✅' },
            { l:'Pending',   v:`Rs ${Number(stats?.total_pending||0).toLocaleString()}`,      c:'#F59E0B', e:'🕐' },
            { l:'Overdue',   v:stats?.overdue_count||0,                                       c:'#EF4444', e:'⚠️' },
            { l:'Submitted', v:stats?.submitted_count||0,                                     c:'#7C3AED', e:'📤' },
            { l:'PDF Slips', v:stats?.pdf_vouchers||0,                                        c:'#0891B2', e:'📄' },
          ].map(s => (
            <div key={s.l} style={{ background:'#fff', borderRadius:12, border:'1px solid #E5E7EB', padding:'14px 16px' }}>
              <div style={{ fontSize:20, marginBottom:4 }}>{s.e}</div>
              <div style={{ fontSize:18, fontWeight:900, color:s.c }}>{s.v}</div>
              <div style={{ fontSize:11, color:'#6B7280' }}>{s.l}</div>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div style={{ display:'flex', background:'#F1F5F9', borderRadius:12, padding:4, marginBottom:20, width:'fit-content', gap:2 }}>
          {[{k:'vouchers',l:'💰 Vouchers'},{k:'structures',l:'📐 Structures'}].map(t => (
            <button key={t.k} onClick={()=>setTab(t.k)}
              style={{ padding:'8px 18px', borderRadius:9, border:'none', background:tab===t.k?'#fff':'transparent', color:tab===t.k?'#1E293B':'#64748B', fontWeight:700, fontSize:13, cursor:'pointer', boxShadow:tab===t.k?'0 1px 4px rgba(0,0,0,0.08)':'none' }}>
              {t.l}
            </button>
          ))}
        </div>

        {/* ── STRUCTURES TAB ── */}
        {tab === 'structures' && (
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(280px,1fr))', gap:14 }}>
            {structures.length === 0
              ? <div style={{ gridColumn:'1/-1', padding:48, textAlign:'center', background:'#fff', borderRadius:16, border:'1px solid #E5E7EB', color:'#9CA3AF' }}>No fee structures yet. Create one to start.</div>
              : structures.map(s => (
                <div key={s.id} style={{ background:'#fff', borderRadius:16, border:'1px solid #E5E7EB', overflow:'hidden' }}>
                  <div style={{ padding:'16px 18px', background:'linear-gradient(135deg,#4F8EF710,#7C3AED08)', borderBottom:'1px solid #F3F4F6' }}>
                    <div style={{ fontWeight:800, color:'#111827', fontSize:15, marginBottom:3 }}>{s.name}</div>
                    {s.academic_year && <div style={{ fontSize:12, color:'#6B7280' }}>{s.academic_year}</div>}
                    {s.due_date && <div style={{ fontSize:12, color:'#EF4444', fontWeight:600 }}>Due: {s.due_date}</div>}
                  </div>
                  <div style={{ padding:'12px 18px' }}>
                    {(s.lms_fee_items||[]).map(i => (
                      <div key={i.id} style={{ display:'flex', justifyContent:'space-between', fontSize:12, color:'#374151', padding:'3px 0', borderBottom:'1px solid #F9FAFB' }}>
                        <span>{i.name}</span>
                        <span style={{ fontWeight:700 }}>Rs {Number(i.amount).toLocaleString()}</span>
                      </div>
                    ))}
                    <div style={{ display:'flex', justifyContent:'space-between', marginTop:8, fontWeight:800, fontSize:14, color:'#111827' }}>
                      <span>Total</span>
                      <span style={{ color:'#4F8EF7' }}>Rs {(s.total_amount||0).toLocaleString()}</span>
                    </div>
                    {s.kuickpay_institution_id && <div style={{ marginTop:6, fontSize:11, color:'#10B981', fontWeight:600 }}>✅ KuickPay configured</div>}
                  </div>
                  <div style={{ padding:'0 18px 14px', display:'flex', gap:8 }}>
                    <button onClick={() => { setEditStruct(s); setShowStructForm(true); }}
                      style={{ flex:1, padding:'7px 0', borderRadius:8, border:'1px solid #E5E7EB', background:'#F9FAFB', color:'#374151', fontSize:12, fontWeight:600, cursor:'pointer' }}>
                      ✏️ Edit
                    </button>
                    <button onClick={() => { if(window.confirm('Delete this structure?')) supabase.from('lms_fee_structures').update({is_active:false}).eq('id',s.id).then(loadAll); }}
                      style={{ padding:'7px 12px', borderRadius:8, border:'1px solid #FECACA', background:'#FEF2F2', cursor:'pointer' }}>
                      <Trash2 size={13} color="#EF4444"/>
                    </button>
                  </div>
                </div>
              ))}
          </div>
        )}

        {/* ── VOUCHERS TAB ── */}
        {tab === 'vouchers' && (
          <>
            {/* Filters */}
            <div style={{ background:'#fff', borderRadius:14, border:'1px solid #E5E7EB', padding:12, marginBottom:14, display:'flex', gap:10, flexWrap:'wrap', alignItems:'center' }}>
              <div style={{ position:'relative', flex:1, minWidth:200 }}>
                <Search size={14} color="#9CA3AF" style={{ position:'absolute', left:9, top:'50%', transform:'translateY(-50%)', pointerEvents:'none' }}/>
                <input value={searchInput} onChange={e=>hs(e.target.value)} placeholder="Search student, roll no, class, structure..." style={{ ...INP, paddingLeft:28 }}/>
              </div>
              <select value={statusF} onChange={e=>setStatusF(e.target.value)} style={{ ...INP, width:'auto' }}>
                <option value="">All Status</option>
                {Object.entries(SS).map(([k,v]) => <option key={k} value={k}>{v.emoji} {v.label}</option>)}
              </select>
              <select value={subStatusF} onChange={e=>setSubStatusF(e.target.value)} style={{ ...INP, width:'auto' }}>
                <option value="">All Submissions</option>
                {Object.entries(SUBSS).map(([k,v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
              <select value={typeF} onChange={e=>setTypeF(e.target.value)} style={{ ...INP, width:'auto' }}>
                <option value="">All Types</option>
                <option value="manual">📋 Manual</option>
                <option value="pdf">📄 PDF Slip</option>
              </select>
              {(search||statusF||subStatusF||typeF) && (
                <button onClick={()=>{setSearchInput('');setSearch('');setStatusF('');setSubStatusF('');setTypeF('');}}
                  style={{ padding:'8px 12px', borderRadius:8, border:'1px solid #E5E7EB', background:'#F9FAFB', cursor:'pointer', fontSize:12 }}>
                  <X size={13}/> Clear
                </button>
              )}
              <span style={{ fontSize:12, color:'#6B7280', fontWeight:600 }}>{filtVouchers.length}/{vouchers.length}</span>
            </div>

            {loading ? (
              <div style={{ padding:48, textAlign:'center', color:'#9CA3AF' }}>Loading...</div>
            ) : filtVouchers.length === 0 ? (
              <div style={{ padding:48, textAlign:'center', background:'#fff', borderRadius:16, border:'1px solid #E5E7EB', color:'#9CA3AF' }}>No vouchers found</div>
            ) : (
              <div style={{ background:'#fff', borderRadius:16, border:'1px solid #E5E7EB', overflow:'auto' }}>
                <table style={{ width:'100%', borderCollapse:'collapse', minWidth:900 }}>
                  <thead>
                    <tr style={{ background:'#F8FAFF', borderBottom:'1px solid #E5E7EB' }}>
                      {['#','Student','Roll No','Class','Fee / Amount','Due','Type','Status','Submission','Actions'].map(h => (
                        <th key={h} style={{ padding:'11px 14px', textAlign:'left', fontSize:11, fontWeight:800, color:'#6B7280', whiteSpace:'nowrap' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filtVouchers.map((v, i) => {
                      const sc  = SS[v.status] || SS.pending;
                      const ssc = SUBSS[v.submission_status] || SUBSS.not_submitted;
                      const isPdf = v.fee_slip_type === 'pdf';
                      const subPending = v.submission_status === 'submitted';
                      return (
                        <tr key={v.id} style={{ borderBottom:i<filtVouchers.length-1?'1px solid #F1F5F9':'none', background:subPending?'#FFFBF0':'transparent' }}>
                          <td style={{ padding:'10px 14px', fontSize:11, color:'#9CA3AF' }}>{i+1}</td>
                          <td style={{ padding:'10px 14px' }}>
                            <div style={{ fontWeight:700, color:'#111827', fontSize:13 }}>{v.student_name}</div>
                            {v.student_username && <div style={{ fontSize:10, color:'#9CA3AF' }}>@{v.student_username}</div>}
                          </td>
                          <td style={{ padding:'10px 14px' }}>
                            {v.student_roll_number
                              ? <code style={{ fontSize:11, fontWeight:800, color:'#4F8EF7', background:'#EFF6FF', padding:'2px 7px', borderRadius:4 }}>{v.student_roll_number}</code>
                              : <span style={{ color:'#D1D5DB' }}>—</span>}
                          </td>
                          <td style={{ padding:'10px 14px', fontSize:12, color:'#374151' }}>{v.student_class||'—'}</td>
                          <td style={{ padding:'10px 14px' }}>
                            <div style={{ fontSize:12, color:'#6B7280' }}>{v.fee_structure_name}</div>
                            <div style={{ fontSize:14, fontWeight:900, color:'#111827' }}>Rs {Number(v.final_amount||v.total_amount).toLocaleString()}</div>
                            {v.discount_amount > 0 && <div style={{ fontSize:10, color:'#10B981' }}>-Rs {Number(v.discount_amount).toLocaleString()} discount</div>}
                          </td>
                          <td style={{ padding:'10px 14px', fontSize:12, color: v.due_date && new Date(v.due_date)<new Date() && v.status!=='paid' ? '#EF4444':'#374151', fontWeight: v.due_date && new Date(v.due_date)<new Date() && v.status!=='paid' ? 700:400 }}>
                            {v.due_date || '—'}
                          </td>
                          <td style={{ padding:'10px 14px' }}>
                            {isPdf
                              ? <a href={v.fee_slip_pdf_url} target="_blank" rel="noreferrer"
                                  style={{ display:'inline-flex', alignItems:'center', gap:4, fontSize:11, fontWeight:700, color:'#0891B2', background:'#ECFEFF', padding:'3px 8px', borderRadius:4, border:'1px solid #A5F3FC', textDecoration:'none' }}>
                                  <FileText size={11}/> PDF
                                </a>
                              : <span style={{ fontSize:11, color:'#6B7280', background:'#F3F4F6', padding:'3px 8px', borderRadius:4 }}>📋 Manual</span>}
                          </td>
                          <td style={{ padding:'10px 14px' }}>
                            <span style={{ fontSize:10, fontWeight:800, color:sc.color, background:sc.bg, border:`1px solid ${sc.border}`, padding:'3px 8px', borderRadius:5 }}>
                              {sc.emoji} {sc.label}
                            </span>
                          </td>
                          <td style={{ padding:'10px 14px' }}>
                            <span style={{ fontSize:10, fontWeight:700, color:ssc.color, background:ssc.bg, padding:'3px 8px', borderRadius:5 }}>
                              {subPending && '⚠️ '}
                              {ssc.label.split('—')[0].trim()}
                            </span>
                          </td>
                          <td style={{ padding:'8px 12px' }}>
                            <div style={{ display:'flex', gap:4, flexWrap:'wrap' }}>
                              {subPending && (
                                <button onClick={() => { setSelVoucher(v); setShowSubModal(true); }}
                                  style={{ padding:'5px 10px', borderRadius:6, border:'none', background:'#F59E0B', color:'#fff', fontSize:10, fontWeight:800, cursor:'pointer', whiteSpace:'nowrap' }}>
                                  👁 Review
                                </button>
                              )}
                              {v.status !== 'paid' && !subPending && (
                                <select onChange={e => { if(e.target.value) markPaidManually(v,e.target.value); e.target.value=''; }}
                                  style={{ fontSize:10, padding:'4px 6px', borderRadius:6, border:'1px solid #BBF7D0', background:'#F0FDF4', color:'#065F46', cursor:'pointer', outline:'none' }}>
                                  <option value="">Mark Paid ▼</option>
                                  <option value="cash">Cash</option>
                                  <option value="bank_transfer">Bank Transfer</option>
                                  <option value="online">Online</option>
                                  <option value="cheque">Cheque</option>
                                </select>
                              )}
                              <button onClick={() => deleteVoucher(v.id)}
                                style={{ padding:'5px 8px', borderRadius:6, border:'1px solid #FECACA', background:'#FEF2F2', cursor:'pointer' }}>
                                <Trash2 size={11} color="#EF4444"/>
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {/* Modals */}
        {showStructForm  && <StructureFormModal  key={editStruct?.id||'new-s'}  editStruct={editStruct}  institutionId={institution?.id} account={account} onSave={onSaved} onClose={()=>{setShowStructForm(false);setEditStruct(null);}}/>}
        {showVoucherForm && <VoucherFormModal    key="new-v"                    structures={structures}  institutionId={institution?.id} account={account} onSave={onSaved} onClose={()=>setShowVoucherForm(false)}/>}
        {showSubModal    && selVoucher && <SubmissionModal key={selVoucher.id}  voucher={selVoucher}     account={account}               onSave={onSaved} onClose={()=>{setShowSubModal(false);setSelVoucher(null);}}/>}

      </LMSPageGuard>
    </LMSLayout>
  );
}